import React from 'react';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { styled } from '@mui/material/styles';

const ModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '80%',
  height: '90%',
  backgroundColor: theme.palette.background.paper,
  boxShadow: 24,
  borderRadius: '10px',
  padding: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  maxHeight: '56px',
  backgroundColor: theme.palette.background.subtle,
  color: theme.palette.text.primary,
}));

const StyledToolbar = styled(Toolbar)({
  minHeight: '56px',
  height: '56px',
  padding: '0 16px',
});

const ContentWrapper = styled(Box)({
  flexGrow: 1,
  overflow: 'hidden',
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  flexGrow: 1,
  marginRight: '8px',
  '& .MuiInputBase-root': {
    height: '36px',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '18px',
    paddingLeft: '8px',
    paddingRight: '8px',
  },
  '& .MuiInputBase-input': {
    fontSize: '14px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

const ActionButton = styled(IconButton)({
  width: '36px',
  height: '36px',
  marginLeft: '8px',
});

/**
 * Preview modal for v2 form-like components (form_step / dynamic_form / dynamic_review_task).
 * Renders an iframe pointed at either the single-form preview surface or, when the caller has
 * bootstrapped a test flow session via {@code POST /form-flow/start-test-session}, the
 * multi-step {@code /form-flow-test/:formSessionId} flow shell:
 *
 * - {@code src} omitted / standalone form → {@code /dynamic-form/:componentId?preview=true}.
 *   The iframe stays on that URL after submit.
 * - {@code src} provided (flow-test deep-link) → the author lands directly on the flow shell
 *   with the entry step as CURRENT and the stepper rendered, rather than seeing the bare
 *   single-form chrome first and only getting the shell after the first submit.
 *
 * Using an iframe (vs. embedding {@code PublicFormPage} / {@code FormFlowPage} directly) is
 * deliberate — a flow handoff inside an embedded component would navigate the outer
 * admin-dashboard, dumping the author out of the canvas.
 */
const DynamicFormModal = ({ open, onClose, componentId, preview = false, src }) => {
  const origin = window.location.origin.replace(/\/+$/, '');
  const displayUrl = src
    || `${origin}/dynamic-form/${componentId}${preview ? '?preview=true' : ''}`;

  const copyUrl = () => {
    navigator.clipboard.writeText(displayUrl);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="dynamic-form-modal"
      aria-describedby="dynamic-form-in-modal"
    >
      <ModalBox>
        <StyledAppBar position="static" elevation={0}>
          <StyledToolbar>
            <StyledTextField
              value={displayUrl}
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
            />
            <ActionButton color="inherit" onClick={copyUrl} aria-label="copy url">
              <ContentCopyIcon />
            </ActionButton>
            <ActionButton
              color="inherit"
              onClick={() => window.open(displayUrl, '_blank', 'noopener,noreferrer')}
              aria-label="open in new tab"
            >
              <OpenInNewIcon />
            </ActionButton>
            <ActionButton edge="end" color="inherit" onClick={onClose} aria-label="close">
              <CloseIcon />
            </ActionButton>
          </StyledToolbar>
        </StyledAppBar>
        <ContentWrapper>
          {open && (
            <iframe
              title="Form preview"
              src={displayUrl}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}
        </ContentWrapper>
      </ModalBox>
    </Modal>
  );
};

export default DynamicFormModal;
